import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { mergeMap, Subject, Subscription, tap } from 'rxjs';
import { AuthResourceService, GenericResourceService } from '@mri/app/resources';
import { AuthCache, Consolidate, CurrentMobile, Device, Mobile, Position } from '@mri/types';
import { AssetPipe } from '../../pipes/asset.pipe';
import { FindOfArrayPipe, IsDisconnectPipe, RenderSpeedPipe } from '../../pipes';
import { DurationTextModule } from '../duration-text';
import { SpeedometerModule } from '../speedometer';
import { CardComponent } from '../../@theme/components/card/card.component';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { Socket } from 'ngx-socket-io';
import { SensorConfig } from '../../../types/sensor-config';
import { MatDialog } from '@angular/material/dialog';
import { SendCommandDialogComponent } from '../send-command-dialog';
import { CommandResumeViewComponent } from '../command-resume-view';
import { MatMenuModule } from '@angular/material/menu';
import { ListMapDataSetComponent } from '../list-map-data-set';
import { PositionShowComponent } from '../position-show';
import { Router, RouterLink } from '@angular/router';
import { getTodayDate } from '@mri/functions';
import { ResumeCurrentMobileDayModule } from '../resume-current-mobile-day';
import { CurrentMobileResourceService } from '../../resources/current-mobile-resource';

@Component({
  selector: 'app-current-mobile-detail',
  standalone: true,
  imports: [
    AssetPipe,
    RenderSpeedPipe,
    IsDisconnectPipe,
    DurationTextModule,
    CardComponent,
    MatButtonModule,
    SpeedometerModule,
    MatDividerModule,
    FindOfArrayPipe,
    CommandResumeViewComponent,
    MatMenuModule,
    RouterLink,
    ResumeCurrentMobileDayModule
  ],
  templateUrl: './current-mobile-detail.component.html',
  styleUrl: './current-mobile-detail.component.scss'
})
export class CurrentMobileDetailComponent implements OnInit, OnDestroy {
  private _dialog = inject(MatDialog);

  private _subscriptions = new Subscription();
  private _elementId$ = new Subject<string>();
  private _currentMobileId: string;
  private _socket?: Socket;
  private _element$ = new Subject<CurrentMobile>();

  isLoading?: boolean;
  currentElement?: CurrentMobile;
  position?: Position;
  device?: Device;
  mobile?: Mobile;
  consolidate?: Consolidate;
  sensors?: SensorConfig[];

  @Input() set elementId(value: string) {
    this._currentMobileId = value;
    this._elementId$.next(value);
  }

  constructor(
    private _listComponent: ListMapDataSetComponent,
    private _genericResourceSrv: GenericResourceService,
    private _authMngSrv: AuthResourceService,
    private _router: Router,
    private _currentSrc: CurrentMobileResourceService
  ) {

  }

  ngOnInit() {
    this._subscriptions.add(
      this._element$
        .pipe(
          tap(object => {
            const { device, mobile, position, consolidate, hardware } = object;

            this.currentElement = object;
            this.sensors = [...(mobile?.config?.sensors || []), ...(hardware?.config?.sensors || [])];
            this.device = device;
            this.mobile = mobile;
            this.position = position;
            this.consolidate = consolidate;
          })
        )
        .subscribe()
    );

    this._subscriptions.add(
      this._elementId$
        .pipe(
          mergeMap(elementId => this._genericResourceSrv.getElement<CurrentMobile>('mobiles/currents', elementId)),
          tap(response => this._element$.next(response.object))
        )
        .subscribe()
    );

    this._elementId$.next(this._currentMobileId);

    this._subscriptions.add(
      this._authMngSrv.authFirst$
        .pipe(
          tap(auth => auth && this._initSocket(auth) )
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }

  sendCommand() {
    this._dialog.open(SendCommandDialogComponent, {
      data: { elementId: this.currentElement?._id },
      autoFocus: false, disableClose: true
    });
  }

  showPosition() {
    //this._matDrawerParent?.close();

    this._listComponent.openDrawer(
      `Posicion actual de ${this.currentElement?.mobile?.name || this.currentElement?.device.uniqueId}`,
      PositionShowComponent,
      { element: this.currentElement?.position }
    );

  }

  toTravelToday() {
    const dateString = getTodayDate();
    const { device } = this.currentElement || {};
    if (!device) return;

    const timezone = '-05:00';

    this._router.navigate(['/console/reports/positions/list'], {
      queryParams: {
        '$view:mode': 'map',
        '$filter:deviceId': device?._id,
        '$filter:endDeviceTime': `${dateString}T23:59:59${timezone}`,
        '$filter:startDeviceTime': `${dateString}T00:00:00${timezone}`,
        '$view:initLoad': true
      }
    });
  }

  toActivityToday() {
    const dateString = getTodayDate();
    const { mobile } = this.currentElement || {};
    if (!mobile) return;

    this._router.navigate(['/console/reports/activities/list'], {
      queryParams: {
        '$filter:deviceId': this.currentElement?.device?._id,
        '$filter:startDay': `${dateString}`,
        '$filter:endDay': `${dateString}`,
        '$view:initLoad': true
      }
    });
  }

  toConsolidatesToday() {
    const [dateString] = new Date().toJSON().split('T');
    const { mobile } = this.currentElement || {};
    if (!mobile) return;

    this._router.navigate(['/console/reports/consolidates/list'], {
      queryParams: {
        '$filter:mobileId': this.currentElement?.mobile?._id,
        '$filter:startDay': `${dateString}`,
        '$filter:endDay': `${dateString}`,
        '$view:initLoad': true
      }
    });
  }


  private _initSocket(auth: AuthCache) {

    this._subscriptions.add(
      this._currentSrc.subscribeElement(this._currentMobileId)
        .pipe(
          tap(element => this._element$.next(element))
        ).subscribe()
    );


  }

  protected readonly Array = Array;


}
